// DocsHubPage — the `/documents` route (Documents library). One explorer for
// living plant markdown and versioned binary artifacts. Markdown gets the
// ChatMarkdown viewer + lease-guarded split editor; binaries get download,
// review status/comments, and the same copy-forward revision history.
//
// Selection lives on the URL path so deep links and browser Back behave:
//
//   /documents/<docPath>
//   /documents/f/<folderPath>
//
// Legacy `/docs` URLs redirect here.
//
// This page is the single owner of which mutation runs and which dialog opens
// — the sidebar, viewer, editor, and history panel stay presentational and
// route everything back through `handleAction` / callbacks.
//
// It talks to the proxy directly through `docsHubApi`; docs are branch-agnostic
// org state. The only BFF hop is DOCX/PDF export (`docs-export-routes.ts`).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  downloadDocRevision,
  isDemoMode,
  isMarkdownDoc,
  prefillNewConversationDraft,
  useActiveBranch,
  useAcquireDocLockMutation,
  useAppDispatch,
  useCopyDocFolderMutation,
  useCopyDocMutation,
  useCreateDocFolderMutation,
  useCreateDocMutation,
  useDeleteDocFolderByPathMutation,
  useDeleteDocFolderMutation,
  useDeleteDocMutation,
  useGetDocRevisionQuery,
  useGetIngestJobsPageQuery,
  useListDocFoldersQuery,
  useListDocRevisionsQuery,
  useListDocsQuery,
  useMoveDocFolderMutation,
  useReleaseDocLockMutation,
  useRenameDocFolderMutation,
  useRestoreDocRevisionMutation,
  useUpdateDocContentMutation,
  useUpdateDocMutation,
  type Doc,
  type DocFolder,
  type DocRevisionMeta
} from '@axilon-apps/domains-ontology-ui-state'

import { useChatAppContext } from '../../chat-app-context'
import {
  ActionErrorDialog,
  countPrefixDescendants,
  DeleteItemDialog,
  expandAncestors,
  hasTextSelection,
  isPathPrefix,
  joinChildPath,
  joinUnderParent,
  leafSegment,
  MoveItemDialog,
  NameDialog,
  parentPathOf,
  PASTE_REFUSED,
  readMutationError,
  relocateUnderPrefix,
  relocatedCreatedPaths,
  RenameItemDialog,
  resolvePasteTarget,
  resolveUndo,
  subtreeFolderPaths,
  useExplorerClipboard,
  useExplorerUndo
} from '../../components/explorer'
import { Button } from '../../components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog'
import { parsePositivePageNumber } from '../../lib/document-preview'
import { downloadName } from './doc-helpers'
import BinaryDocPane from './BinaryDocPane'
import ConflictDialog from './ConflictDialog'
import DocEditor from './DocEditor'
import DocsExplorerSidebar, { type DocsSidebarAction } from './DocsExplorerSidebar'
import DocViewer from './DocViewer'
import VersionHistoryPanel from './VersionHistoryPanel'
import { LazyMarkdownDiff } from './LazyMarkdownEditor'
import {
  buildContentSavePayload,
  buildDocRefreshInstruction,
  buildRestorePayload,
  clockTime,
  docRenameInput,
  readErrorMessage,
  readLockConflict,
  readRevisionConflict,
  withMarkdownSuffix,
  type RevisionConflict
} from './doc-helpers'
import { DOCS_SUPERSEDED_PARAMS, buildDocsAppContextEntries } from './docs-app-context'
import type { DocumentsOutletContext } from './DocumentsHomePage'
import { DOCS_HOME, docFolderHref, docHref } from './explorer-paths'
import {
  INGESTED_DOCUMENTS_FOLDER_PATH,
  isIngestedDocumentsPath,
  withIngestedDocumentsVirtual,
  writableDocsCreateParent
} from './ingested-documents-virtual'
import { useDocsAuthor } from './use-docs-author'
import { useEditLease, type EditLeaseTransport } from './use-edit-lease'
import { useExplorerSelection } from './useExplorerSelection'

/** Revision rows kept in the history panel; the proxy caps a page at 200. */
const REVISION_PAGE_SIZE = 50

interface TargetState {
  kind: 'doc' | 'folder'
  path: string
  name: string
  doc?: Doc
  folder?: DocFolder
}

interface MoveState {
  kind: 'folder' | 'doc'
  path: string
  name: string
  doc?: Doc
}

const DocsHubPage = () => {
  const navigate = useNavigate()
  const { tabsSlot } = useOutletContext<DocumentsOutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()
  const sourcePage = parsePositivePageNumber(searchParams.get('page'))
  const routeSelection = useExplorerSelection()

  const author = useDocsAuthor()

  const activeBranchId = useActiveBranch()
  const showIngestOverview =
    routeSelection.kind === 'folder' && routeSelection.path === INGESTED_DOCUMENTS_FOLDER_PATH
  const folderList = useListDocFoldersQuery()
  const docList = useListDocsQuery()
  const ingestJobs = useGetIngestJobsPageQuery(
    {
      kind: 'unstructured',
      associatedBranchId: activeBranchId ?? '',
      pageSize: 100
    },
    {
      skip: !showIngestOverview || activeBranchId === undefined,
      refetchOnMountOrArgChange: true
    }
  )
  const folders = useMemo<DocFolder[]>(() => folderList.data ?? [], [folderList.data])
  const docs = useMemo<Doc[]>(() => docList.data ?? [], [docList.data])
  const explorerFolders = useMemo(
    () => withIngestedDocumentsVirtual(folders, docs),
    [folders, docs]
  )
  const isLoading = folderList.isLoading || docList.isLoading

  // Legacy `?doc=<uuid>` / `?folder=<path>` → path URLs.
  useEffect(() => {
    if (isLoading) return
    const legacyDocId = searchParams.get('doc')
    const legacyFolder = searchParams.get('folder')
    if (legacyDocId === null && legacyFolder === null) return

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('doc')
    nextParams.delete('folder')
    const search = nextParams.toString()
    const suffix = search === '' ? '' : `?${search}`

    if (legacyDocId) {
      const found = docs.find((doc) => doc.id === legacyDocId)
      if (found) {
        navigate(`${docHref(found.path)}${suffix}`, { replace: true })
        return
      }
      setSearchParams(nextParams, { replace: true })
      return
    }

    if (legacyFolder) {
      navigate(`${docFolderHref(legacyFolder)}${suffix}`, { replace: true })
      return
    }

    setSearchParams(nextParams, { replace: true })
  }, [isLoading, searchParams, docs, navigate, setSearchParams])

  const selectedDoc = useMemo(() => {
    if (routeSelection.kind !== 'item') return null
    return docs.find((doc) => doc.path === routeSelection.path) ?? null
  }, [routeSelection, docs])
  const selectedIsMarkdown = selectedDoc === null || isMarkdownDoc(selectedDoc)

  const selectedDocId = selectedDoc?.id ?? ''
  const folderParam = routeSelection.kind === 'folder' ? routeSelection.path : ''

  const [createDocFolder] = useCreateDocFolderMutation()
  const [renameDocFolder] = useRenameDocFolderMutation()
  const [deleteDocFolder] = useDeleteDocFolderMutation()
  const [deleteDocFolderByPath] = useDeleteDocFolderByPathMutation()
  const [moveDocFolder] = useMoveDocFolderMutation()
  const [copyDocFolder] = useCopyDocFolderMutation()
  const [createDoc] = useCreateDocMutation()
  const [copyDoc] = useCopyDocMutation()
  const [updateDoc] = useUpdateDocMutation()
  const [deleteDoc] = useDeleteDocMutation()
  const [updateDocContent] = useUpdateDocContentMutation()
  const [acquireDocLock] = useAcquireDocLockMutation()
  const [releaseDocLock] = useReleaseDocLockMutation()
  const [restoreDocRevision] = useRestoreDocRevisionMutation()

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [filter, setFilter] = useState('')
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [createDialog, setCreateDialog] = useState<{
    kind: 'doc' | 'folder'
    parentPath: string
  } | null>(null)
  const [renameDialog, setRenameDialog] = useState<TargetState | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<TargetState | null>(null)
  const [moveDialog, setMoveDialog] = useState<MoveState | null>(null)
  const [actionError, setActionError] = useState<{ title: string; message: string } | null>(null)
  const [conflict, setConflict] = useState<RevisionConflict | null>(null)
  const [compareRevision, setCompareRevision] = useState<DocRevisionMeta | null>(null)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draftResetKey, setDraftResetKey] = useState(0)
  const [editorDirty, setEditorDirty] = useState(false)
  const editorDirtyRef = useRef(false)

  useEffect(() => {
    editorDirtyRef.current = editorDirty
  }, [editorDirty])

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent): void => {
      if (editorDirtyRef.current) e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  const dispatch = useAppDispatch()
  const canWrite = !isDemoMode()
  const clipboard = useExplorerClipboard()
  const undo = useExplorerUndo()
  const pasteInFlight = useRef(false)
  const undoInFlight = useRef(false)
  const codeDocs = useMemo(() => docs.map((doc) => ({ ...doc, code: doc.content })), [docs])

  // Switching docs (or losing the open one to a delete) always lands in the
  // viewer; the lease hook releases the previous doc's lock as its id changes.
  useEffect(() => {
    setMode('view')
    setConflict(null)
    setCompareRevision(null)
    setEditorDirty(false)
  }, [selectedDocId])

  const revisionQuery = useListDocRevisionsQuery(
    { docId: selectedDocId, page_size: REVISION_PAGE_SIZE },
    { skip: selectedDocId === '' }
  )
  const revisions = revisionQuery.data?.revisions ?? []
  const hasMoreRevisions = (revisionQuery.data?.next_page_token ?? '') !== ''

  const compareQuery = useGetDocRevisionQuery(
    { docId: selectedDocId, revisionId: compareRevision?.id ?? '' },
    { skip: compareRevision === null || selectedDocId === '' || !selectedIsMarkdown }
  )

  useChatAppContext(
    buildDocsAppContextEntries({
      doc:
        selectedDoc === null
          ? undefined
          : { name: selectedDoc.name, path: selectedDoc.path, id: selectedDoc.id },
      folder: folderParam,
      editing: mode === 'edit'
    }),
    DOCS_SUPERSEDED_PARAMS
  )

  const leaseTransport = useMemo<EditLeaseTransport>(
    () => ({
      acquire: (input) => acquireDocLock({ id: selectedDocId, input }).unwrap(),
      release: (lockToken) => releaseDocLock({ id: selectedDocId, lock_token: lockToken }).unwrap()
    }),
    [acquireDocLock, releaseDocLock, selectedDocId]
  )
  const lease = useEditLease({
    docId: selectedDocId === '' ? null : selectedDocId,
    active: mode === 'edit',
    holderName: author,
    transport: leaseTransport
  })

  const confirmDiscardIfDirty = useCallback((): boolean => {
    if (!editorDirtyRef.current) return true
    return window.confirm('You have unsaved changes. Discard them?')
  }, [])

  const selectDoc = useCallback(
    (id: string): void => {
      if (!confirmDiscardIfDirty()) return
      if (id === '') {
        navigate(DOCS_HOME, { replace: true })
        return
      }
      const found = docs.find((doc) => doc.id === id)
      if (!found) return
      navigate(docHref(found.path), { replace: true })
    },
    [confirmDiscardIfDirty, docs, navigate]
  )

  const selectFolder = useCallback(
    (path: string): void => {
      if (!confirmDiscardIfDirty()) return
      navigate(docFolderHref(path), { replace: true })
    },
    [confirmDiscardIfDirty, navigate]
  )

  const handleToggle = (path: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const defaultParentForHeaderCreate = useMemo(() => {
    if (folderParam) return writableDocsCreateParent(folderParam)
    if (selectedDoc) return writableDocsCreateParent(parentPathOf(selectedDoc.path))
    return ''
  }, [folderParam, selectedDoc])

  const goHomeIfUnder = (prefix: string): void => {
    if (folderParam && isPathPrefix(prefix, folderParam)) {
      navigate(DOCS_HOME, { replace: true })
    }
    if (selectedDoc && isPathPrefix(prefix, selectedDoc.path)) {
      navigate(DOCS_HOME, { replace: true })
    }
  }

  const afterFolderMoved = (fromPath: string, toPath: string): void => {
    setExpanded((prev) => expandAncestors(prev, toPath))
    if (folderParam && isPathPrefix(fromPath, folderParam)) {
      navigate(docFolderHref(relocateUnderPrefix(fromPath, toPath, folderParam)), { replace: true })
    }
    if (selectedDoc && isPathPrefix(fromPath, selectedDoc.path)) {
      navigate(docHref(relocateUnderPrefix(fromPath, toPath, selectedDoc.path)), { replace: true })
    }
  }

  const moveFolderToParent = async (
    fromPath: string,
    toParentPath: string,
    name?: string
  ): Promise<void> => {
    const toPath = joinUnderParent(toParentPath, leafSegment(fromPath))
    if (toPath === fromPath) return
    const displayName =
      name ?? folders.find((row) => row.path === fromPath)?.name ?? leafSegment(fromPath)
    const folderId = folders.find((row) => row.path === fromPath)?.id
    await moveDocFolder({ fromPath, toPath, ...(name !== undefined ? { name } : {}) }).unwrap()
    undo.push({
      op: 'folder-relocate',
      label: `Undo move of "${displayName}"`,
      ...(folderId !== undefined ? { folderId } : {}),
      atPath: toPath,
      toPath: fromPath,
      toName: displayName
    })
    afterFolderMoved(fromPath, toPath)
  }

  const moveDocToParent = async (doc: Doc, toParentPath: string): Promise<void> => {
    const toPath = joinUnderParent(toParentPath, leafSegment(doc.path))
    if (toPath === doc.path) return
    const updated = await updateDoc({ id: doc.id, input: { path: toPath } }).unwrap()
    undo.push({
      op: 'dashboard-relocate',
      label: `Undo move of "${doc.name}"`,
      id: doc.id,
      atPath: updated.path,
      toPath: doc.path,
      toName: doc.name
    })
    setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
    navigate(docHref(updated.path), { replace: true })
  }

  const handlePaste = async (destPath: string): Promise<void> => {
    if (!canWrite || pasteInFlight.current) return
    if (isIngestedDocumentsPath(destPath)) {
      setActionError({
        title: "Couldn't paste",
        message: 'Ingested Documents is a locked system folder.'
      })
      return
    }
    const result = resolvePasteTarget(clipboard.entry, destPath, folders, docs)
    if (result.kind === 'noop') return
    if (result.kind === 'refused') {
      if (result.reason === PASTE_REFUSED.stale) clipboard.clear()
      setActionError({ title: "Couldn't paste", message: result.reason })
      return
    }
    pasteInFlight.current = true
    try {
      if (result.kind === 'move') {
        if (result.type === 'folder') {
          await moveFolderToParent(result.fromPath, result.toParentPath)
        } else {
          const doc = docs.find((row) => row.id === result.id)
          if (!doc) {
            clipboard.clear()
            setActionError({ title: "Couldn't paste", message: PASTE_REFUSED.stale })
            return
          }
          await moveDocToParent(doc, result.toParentPath)
        }
        clipboard.clear()
        return
      }
      if (result.type === 'folder') {
        const createdPaths = relocatedCreatedPaths(result.fromPath, result.toPath, folders, docs)
        const copied = await copyDocFolder({
          fromPath: result.fromPath,
          toPath: result.toPath,
          name: result.name
        }).unwrap()
        undo.push({
          op: 'remove-folder',
          label: `Undo copy of "${result.name}"`,
          atPath: copied.toPath,
          createdPaths
        })
        setExpanded((prev) => expandAncestors(prev, copied.toPath))
        selectFolder(copied.toPath)
        return
      }
      const source = docs.find((row) => row.id === result.id)
      const copied = await copyDoc({
        id: result.id,
        input: { name: result.name, path: result.path }
      }).unwrap()
      undo.push({
        op: 'remove-dashboard',
        label: `Undo copy of "${copied.name}"`,
        id: copied.id,
        atPath: copied.path,
        expectCode: source?.content ?? copied.content
      })
      setExpanded((prev) => expandAncestors(prev, parentPathOf(copied.path)))
      selectDoc(copied.id)
    } catch (err) {
      setActionError({ title: "Couldn't paste", message: readMutationError(err) })
    } finally {
      pasteInFlight.current = false
    }
  }
  const handlePasteRef = useRef(handlePaste)
  handlePasteRef.current = handlePaste

  const handleUndo = async (): Promise<void> => {
    if (!canWrite || undoInFlight.current) return
    while (true) {
      const entry = undo.pop()
      if (entry === undefined) return
      const result = resolveUndo(entry, folders, codeDocs)
      if (result.kind === 'stale') continue
      if (result.kind === 'blocked') {
        setActionError({ title: "Couldn't undo", message: result.reason })
        return
      }
      undoInFlight.current = true
      try {
        const applied = result.entry
        if (applied.op === 'folder-relocate') {
          if (
            applied.atPath === applied.toPath &&
            applied.folderId &&
            applied.toName !== undefined
          ) {
            await renameDocFolder({
              id: applied.folderId,
              input: { name: applied.toName }
            }).unwrap()
          } else {
            await moveDocFolder({
              fromPath: applied.atPath,
              toPath: applied.toPath,
              ...(applied.toName !== undefined ? { name: applied.toName } : {})
            }).unwrap()
            afterFolderMoved(applied.atPath, applied.toPath)
          }
          return
        }
        if (applied.op === 'dashboard-relocate') {
          const updated = await updateDoc({
            id: applied.id,
            input: {
              path: applied.toPath,
              ...(applied.toName !== undefined ? { name: applied.toName } : {})
            }
          }).unwrap()
          setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
          navigate(docHref(updated.path), { replace: true })
          return
        }
        if (applied.op === 'remove-dashboard') {
          await deleteDoc(applied.id).unwrap()
          if (selectedDocId === applied.id) navigate(DOCS_HOME, { replace: true })
          return
        }
        if (applied.op === 'remove-folder') {
          await deleteDocFolderByPath({ path: applied.atPath }).unwrap()
          goHomeIfUnder(applied.atPath)
          return
        }
        if (applied.op === 'restore-dashboard') {
          const created = await createDoc({
            name: applied.name,
            path: applied.path,
            content: applied.code,
            ...(applied.anchors ? { anchor_node_ids: [...applied.anchors] } : {})
          }).unwrap()
          setExpanded((prev) => expandAncestors(prev, parentPathOf(created.path)))
          navigate(docHref(created.path), { replace: true })
          return
        }
        continue
      } catch (err) {
        setActionError({ title: "Couldn't undo", message: readMutationError(err) })
        return
      } finally {
        undoInFlight.current = false
      }
    }
  }
  const handleUndoRef = useRef(handleUndo)
  handleUndoRef.current = handleUndo

  const handleCreate = async (name: string): Promise<void> => {
    if (!createDialog) return
    if (createDialog.kind === 'folder') {
      const path = joinChildPath(createDialog.parentPath, name)
      if (path === '')
        throw new Error('Name must contain at least one letter, digit, dash, or dot.')
      const created = await createDocFolder({ name, path }).unwrap()
      undo.push({
        op: 'remove-folder',
        label: `Undo create of "${name}"`,
        atPath: created.path,
        createdPaths: [created.path]
      })
      setExpanded((prev) => expandAncestors(prev, created.path))
      selectFolder(created.path)
      toast.success(`Created folder “${name}”`)
      return
    }
    const fileName = withMarkdownSuffix(name)
    const path = joinChildPath(createDialog.parentPath, fileName)
    if (path === '') throw new Error('Name must contain at least one letter, digit, dash, or dot.')
    const created = await createDoc({ name: fileName, path }).unwrap()
    undo.push({
      op: 'remove-dashboard',
      label: `Undo create of "${fileName}"`,
      id: created.id,
      atPath: created.path,
      expectCode: created.content
    })
    setExpanded((prev) => expandAncestors(prev, path))
    selectDoc(created.id)
    toast.success(`Created “${fileName}”`)
  }

  const handleRename = async ({ name, path }: { name: string; path?: string }): Promise<void> => {
    if (!renameDialog) return
    if (renameDialog.kind === 'folder') {
      const fromPath = renameDialog.path
      const toPath = path ?? fromPath
      if (renameDialog.folder?.id && toPath === fromPath) {
        await renameDocFolder({ id: renameDialog.folder.id, input: { name } }).unwrap()
        undo.push({
          op: 'folder-relocate',
          label: `Undo rename of "${renameDialog.name}"`,
          folderId: renameDialog.folder.id,
          atPath: fromPath,
          toPath: fromPath,
          toName: renameDialog.name
        })
        toast.success(`Renamed to “${name}”`)
        return
      }
      if (toPath !== fromPath) {
        await moveDocFolder({ fromPath, toPath, name }).unwrap()
        undo.push({
          op: 'folder-relocate',
          label: `Undo rename of "${renameDialog.name}"`,
          ...(renameDialog.folder?.id !== undefined ? { folderId: renameDialog.folder.id } : {}),
          atPath: toPath,
          toPath: fromPath,
          toName: renameDialog.name
        })
        afterFolderMoved(fromPath, toPath)
      }
      toast.success(`Renamed to “${name}”`)
      return
    }
    if (renameDialog.doc) {
      const input = docRenameInput(name, path)
      const updated = await updateDoc({ id: renameDialog.doc.id, input }).unwrap()
      undo.push({
        op: 'dashboard-relocate',
        label: `Undo rename of "${renameDialog.name}"`,
        id: renameDialog.doc.id,
        atPath: updated.path,
        toPath: renameDialog.path,
        toName: renameDialog.name
      })
      if (updated.path !== renameDialog.path) {
        setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
        navigate(docHref(updated.path), { replace: true })
      }
      toast.success(`Renamed to “${input.name}”`)
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (!deleteDialog) return
    if (deleteDialog.kind === 'folder') {
      if (deleteDialog.folder?.id) {
        await deleteDocFolder(deleteDialog.folder.id).unwrap()
      } else {
        await deleteDocFolderByPath({ path: deleteDialog.path }).unwrap()
      }
      // Folder delete has no restore path yet, so drop the stack rather than
      // let a later undo replay an older entry against a changed tree.
      undo.clear()
      goHomeIfUnder(deleteDialog.path)
      toast.success(`Deleted folder “${deleteDialog.name}”`)
      return
    }
    if (deleteDialog.doc) {
      const doc = deleteDialog.doc
      await deleteDoc(doc.id).unwrap()
      undo.push({
        op: 'restore-dashboard',
        label: `Undo delete of "${doc.name}"`,
        name: doc.name,
        path: doc.path,
        code: doc.content,
        anchors: doc.anchor_node_ids
      })
      if (selectedDocId === doc.id) navigate(DOCS_HOME, { replace: true })
      toast.success(`Deleted “${doc.name}”`)
    }
  }

  const handleMove = async (destinationFolderPath: string): Promise<void> => {
    if (!moveDialog) return
    if (isIngestedDocumentsPath(destinationFolderPath)) {
      setActionError({
        title: "Couldn't move",
        message: 'Ingested Documents is a locked system folder.'
      })
      return
    }
    if (moveDialog.kind === 'folder') {
      await moveFolderToParent(moveDialog.path, destinationFolderPath)
      return
    }
    if (moveDialog.doc) {
      await moveDocToParent(moveDialog.doc, destinationFolderPath)
    }
  }

  const handleSave = async ({
    content,
    summary
  }: {
    content: string
    summary: string
  }): Promise<void> => {
    if (selectedDoc === null || lease.token === null) return
    setSaving(true)
    try {
      await updateDocContent({
        id: selectedDoc.id,
        input: buildContentSavePayload({
          content,
          lockToken: lease.token,
          expectedRevisionId: lease.revisionId ?? undefined,
          author,
          summary
        })
      }).unwrap()
      await lease.release()
      setMode('view')
      setEditorDirty(false)
      toast.success('Saved revision')
    } catch (err) {
      const revisionConflict = readRevisionConflict(err)
      if (revisionConflict !== null) {
        setConflict(revisionConflict)
        return
      }
      toast.error(readErrorMessage(err, 'Failed to save the revision'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = (): void => {
    if (!confirmDiscardIfDirty()) return
    void lease.release()
    setMode('view')
    setEditorDirty(false)
  }

  // Discard the local draft, pull the server's current version back in, and
  // re-take the lease so the next save carries a fresh revision id.
  const handleConflictReload = async (): Promise<void> => {
    setConflict(null)
    await docList.refetch()
    void revisionQuery.refetch()
    setDraftResetKey((previous) => previous + 1)
    lease.retry()
  }

  // Restore needs the same guards as a save, so it takes the lease for the
  // duration of the call rather than assuming the doc is unlocked.
  const handleRestore = async (revision: DocRevisionMeta): Promise<void> => {
    if (selectedDoc === null) return
    setRestoringId(revision.id)
    let token: string | null = null
    try {
      const acquired = await acquireDocLock({
        id: selectedDoc.id,
        input: { holder_kind: 'user', holder_name: author }
      }).unwrap()
      token = acquired.lock_token
      await restoreDocRevision({
        docId: selectedDoc.id,
        revisionId: revision.id,
        input: buildRestorePayload({
          lockToken: token,
          expectedRevisionId: acquired.current_revision_id,
          author,
          summary: `Restored the revision from ${revision.author_name}`
        })
      }).unwrap()
      toast.success('Restored — kept as a new revision on top of the history')
    } catch (err) {
      const lockConflict = readLockConflict(err)
      toast.error(
        lockConflict === null
          ? readErrorMessage(err, 'Failed to restore that revision')
          : `${lockConflict.holderName} is editing this doc until ${clockTime(lockConflict.expiresAt)}`
      )
    } finally {
      if (token !== null) {
        await releaseDocLock({ id: selectedDoc.id, lock_token: token })
          .unwrap()
          .catch(() => undefined)
      }
      setRestoringId(null)
    }
  }

  const handleDownloadRevision = async (revision: DocRevisionMeta): Promise<void> => {
    if (selectedDoc === null) return
    try {
      await downloadDocRevision(
        selectedDoc.id,
        revision.id,
        downloadName(selectedDoc.name, revision.format || selectedDoc.format)
      )
    } catch (err) {
      toast.error(readErrorMessage(err, 'Failed to download that revision'))
    }
  }

  const handleUpdateWithAgent = (): void => {
    if (selectedDoc === null) return
    dispatch(prefillNewConversationDraft(buildDocRefreshInstruction(selectedDoc)))
    // The agent sidebar is collapsed by default below 1280px, so the prefilled
    // prompt is otherwise invisible — surface what just happened.
    toast('Prompt drafted in chat sidebar', {
      description: 'Open the chat sidebar on the right to review and send.',
      position: 'top-center'
    })
  }

  const handleAction = (action: DocsSidebarAction): void => {
    switch (action.kind) {
      case 'create-doc':
        setCreateDialog({ kind: 'doc', parentPath: writableDocsCreateParent(action.parentPath) })
        return
      case 'create-folder':
        setCreateDialog({
          kind: 'folder',
          parentPath: writableDocsCreateParent(action.parentPath)
        })
        return
      case 'rename-folder':
        setRenameDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'delete-folder':
        setDeleteDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'move-folder':
        setMoveDialog({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'move-doc':
        setMoveDialog({
          kind: 'doc',
          path: action.doc.path,
          name: action.doc.name,
          doc: action.doc
        })
        return
      case 'drop-folder':
        if (
          isIngestedDocumentsPath(action.fromPath) ||
          isIngestedDocumentsPath(action.toParentPath)
        ) {
          return
        }
        void moveFolderToParent(action.fromPath, action.toParentPath).catch((err) => {
          setActionError({ title: "Couldn't move", message: readMutationError(err) })
        })
        return
      case 'drop-doc':
        if (
          isIngestedDocumentsPath(action.doc.path) ||
          isIngestedDocumentsPath(action.toParentPath)
        ) {
          return
        }
        void moveDocToParent(action.doc, action.toParentPath).catch((err) => {
          setActionError({ title: "Couldn't move", message: readMutationError(err) })
        })
        return
      case 'copy-folder':
        clipboard.copy({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'cut-folder':
        clipboard.cut({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'copy-doc':
        clipboard.copy({
          kind: 'item',
          id: action.doc.id,
          path: action.doc.path,
          name: action.doc.name
        })
        return
      case 'cut-doc':
        clipboard.cut({
          kind: 'item',
          id: action.doc.id,
          path: action.doc.path,
          name: action.doc.name
        })
        return
      case 'paste':
        void handlePaste(action.parentPath)
        return
      case 'undo':
        void handleUndo()
        return
      case 'rename-doc':
        setRenameDialog({
          kind: 'doc',
          path: action.doc.path,
          name: action.doc.name,
          doc: action.doc
        })
        return
      case 'delete-doc':
        setDeleteDialog({
          kind: 'doc',
          path: action.doc.path,
          name: action.doc.name,
          doc: action.doc
        })
        return
    }
  }

  const dialogOpen =
    createDialog !== null ||
    renameDialog !== null ||
    deleteDialog !== null ||
    moveDialog !== null ||
    actionError !== null ||
    conflict !== null ||
    compareRevision !== null

  useEffect(() => {
    if (!canWrite || mode === 'edit') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogOpen) return
      const target = event.target
      if (target instanceof HTMLElement) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return
        }
      }

      const mod = event.metaKey || event.ctrlKey
      if (mod && !event.altKey && !event.shiftKey) {
        const key = event.key.toLowerCase()
        if (key === 'c') {
          if (hasTextSelection()) return
          if (folderParam && isIngestedDocumentsPath(folderParam)) return
          if (selectedDoc && isIngestedDocumentsPath(selectedDoc.path)) return
          if (folderParam) {
            event.preventDefault()
            const folder = folders.find((row) => row.path === folderParam)
            clipboard.copy({
              kind: 'folder',
              path: folderParam,
              name: folder?.name ?? leafSegment(folderParam)
            })
          } else if (selectedDoc) {
            event.preventDefault()
            clipboard.copy({
              kind: 'item',
              id: selectedDoc.id,
              path: selectedDoc.path,
              name: selectedDoc.name
            })
          }
          return
        }
        if (key === 'x') {
          if (folderParam && isIngestedDocumentsPath(folderParam)) return
          if (selectedDoc && isIngestedDocumentsPath(selectedDoc.path)) return
          if (folderParam) {
            event.preventDefault()
            const folder = folders.find((row) => row.path === folderParam)
            clipboard.cut({
              kind: 'folder',
              path: folderParam,
              name: folder?.name ?? leafSegment(folderParam)
            })
          } else if (selectedDoc) {
            event.preventDefault()
            clipboard.cut({
              kind: 'item',
              id: selectedDoc.id,
              path: selectedDoc.path,
              name: selectedDoc.name
            })
          }
          return
        }
        if (key === 'v') {
          if (clipboard.entry === null) return
          event.preventDefault()
          void handlePasteRef.current(defaultParentForHeaderCreate)
          return
        }
        if (key === 'z') {
          if (undo.entries.length === 0) return
          event.preventDefault()
          void handleUndoRef.current()
          return
        }
      }

      if (event.key === 'Escape' && clipboard.entry?.mode === 'cut') {
        event.preventDefault()
        clipboard.clear()
        return
      }

      if (folderParam) {
        if (isIngestedDocumentsPath(folderParam)) return
        const folder = folders.find((row) => row.path === folderParam)
        const name = folder?.name ?? leafSegment(folderParam)
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({ kind: 'folder', path: folderParam, name, folder })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({ kind: 'folder', path: folderParam, name, folder })
        }
        return
      }
      if (selectedDoc) {
        if (isIngestedDocumentsPath(selectedDoc.path)) return
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({
            kind: 'doc',
            path: selectedDoc.path,
            name: selectedDoc.name,
            doc: selectedDoc
          })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({
            kind: 'doc',
            path: selectedDoc.path,
            name: selectedDoc.name,
            doc: selectedDoc
          })
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    canWrite,
    clipboard,
    defaultParentForHeaderCreate,
    dialogOpen,
    folderParam,
    folders,
    mode,
    selectedDoc,
    undo.entries.length
  ])

  const moveDisabledPaths = useMemo(() => {
    const blocked = new Set<string>([INGESTED_DOCUMENTS_FOLDER_PATH])
    if (moveDialog?.kind === 'folder') {
      for (const path of subtreeFolderPaths(moveDialog.path, explorerFolders, docs)) {
        blocked.add(path)
      }
    }
    for (const folder of explorerFolders) {
      if (isIngestedDocumentsPath(folder.path)) blocked.add(folder.path)
    }
    for (const doc of docs) {
      if (!isIngestedDocumentsPath(doc.path)) continue
      for (const path of expandAncestors(new Set(), parentPathOf(doc.path))) {
        blocked.add(path)
      }
    }
    return blocked
  }, [moveDialog, explorerFolders, docs])

  const deleteDescendantCount =
    deleteDialog?.kind === 'folder' ? countPrefixDescendants(deleteDialog.path, folders, docs) : 0

  return (
    /* [contain:size] keeps the docs layout from inflating AppLayout's outlet
       wrapper (same Tags-page pattern): the wrapper is a flex item with
       min-height:auto, so without size containment its floor becomes the
       intrinsic markdown height and `main` scrolls — taking the folder tree
       with it. Containment pins the page to the leftover viewport so the
       explorer stays put and only the document pane scrolls. */
    <div className="flex h-full min-h-0 flex-col overflow-hidden [contain:size]">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <DocsExplorerSidebar
          folders={explorerFolders}
          docs={docs}
          selectedId={selectedDoc?.id ?? null}
          selectedFolder={folderParam || null}
          expanded={expanded}
          onToggle={handleToggle}
          onSelectDoc={selectDoc}
          onSelectFolder={selectFolder}
          onAction={handleAction}
          filter={filter}
          onFilterChange={setFilter}
          isLoading={isLoading}
          createParentPath={defaultParentForHeaderCreate}
          clipboard={clipboard.entry}
          undoLabel={undo.entries.at(-1)?.label ?? null}
          topSlot={tabsSlot}
        />

        <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {isLoading && selectedDoc === null ? (
            <div className="flex items-center gap-2 p-6 text-sm text-ink-3">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading docs…
            </div>
          ) : selectedDoc === null && showIngestOverview ? (
            <div className="h-full overflow-y-auto p-6">
              <div className="mx-auto flex max-w-2xl flex-col gap-4">
                <div>
                  <h2 className="m-0 text-base font-semibold text-ink-1">Ingested Documents</h2>
                  <p className="mb-0 mt-1 text-sm text-ink-3">
                    Documents are published here after their ingest branch is merged. Open an ingest
                    run below to follow processing or review its branch.
                  </p>
                </div>
                {ingestJobs.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-ink-3">
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Loading document ingests…
                  </div>
                ) : (ingestJobs.data?.jobs.length ?? 0) === 0 ? (
                  <div className="rounded-md border border-line p-4 text-sm text-ink-3">
                    No document ingests are associated with the active branch.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {ingestJobs.data?.jobs.map((job) => {
                      const label = job.runName?.trim() || job.fileName || job.id
                      return (
                        <div
                          key={job.id}
                          className="flex items-center justify-between gap-4 rounded-md border border-line p-4"
                        >
                          <div className="min-w-0">
                            <p className="m-0 truncate text-sm font-medium text-ink-1">{label}</p>
                            <p className="mb-0 mt-1 text-xs capitalize text-ink-3">
                              {job.state.replaceAll('_', ' ')}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/ingest?job=${encodeURIComponent(job.id)}`)}
                          >
                            Open ingest
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : selectedDoc === null ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
              <FileText className="size-8 text-ink-3" aria-hidden />
              <p className="m-0 max-w-sm text-sm text-ink-3">
                {docs.length === 0
                  ? 'No documents yet. Create one from the sidebar, or ask the agent to draft a procedure from the ontology.'
                  : 'Select a document from the sidebar to open it.'}
              </p>
            </div>
          ) : !selectedIsMarkdown ? (
            <BinaryDocPane
              doc={selectedDoc}
              sourcePage={sourcePage}
              revisions={revisions}
              hasMoreRevisions={hasMoreRevisions}
              historyOpen={historyOpen}
              onUpdateWithAgent={handleUpdateWithAgent}
              onToggleHistory={() => setHistoryOpen((previous) => !previous)}
            />
          ) : mode === 'edit' ? (
            <DocEditor
              doc={selectedDoc}
              lease={lease}
              saving={saving}
              onSave={handleSave}
              onCancel={handleCancelEdit}
              resetKey={draftResetKey}
              onDirtyChange={setEditorDirty}
            />
          ) : (
            <DocViewer
              doc={selectedDoc}
              revisions={revisions}
              hasMoreRevisions={hasMoreRevisions}
              historyOpen={historyOpen}
              canEdit={canWrite}
              onEdit={() => setMode('edit')}
              onUpdateWithAgent={handleUpdateWithAgent}
              onToggleHistory={() => setHistoryOpen((previous) => !previous)}
            />
          )}
        </div>

        {historyOpen && selectedDoc !== null ? (
          <VersionHistoryPanel
            docId={selectedDoc.id}
            revisions={revisions}
            isLoading={revisionQuery.isLoading}
            hasMore={hasMoreRevisions}
            restoringId={restoringId}
            canRestore={canWrite && mode === 'view'}
            showDiff={selectedIsMarkdown}
            onCompare={selectedIsMarkdown ? setCompareRevision : undefined}
            onRestore={(revision) => void handleRestore(revision)}
            onDownload={
              selectedIsMarkdown ? undefined : (revision) => void handleDownloadRevision(revision)
            }
            onClose={() => setHistoryOpen(false)}
          />
        ) : null}
      </div>

      <NameDialog
        open={createDialog !== null}
        title={createDialog?.kind === 'folder' ? 'New folder' : 'New doc'}
        label={createDialog?.kind === 'folder' ? 'Folder name' : 'Doc name'}
        placeholder={createDialog?.kind === 'folder' ? 'lines' : 'cold-start'}
        submitLabel={createDialog?.kind === 'folder' ? 'Create folder' : 'Create doc'}
        hint={
          createDialog === null
            ? undefined
            : `Lands in /${createDialog.parentPath === '' ? '' : `${createDialog.parentPath}/`}`
        }
        onClose={() => setCreateDialog(null)}
        onSubmit={handleCreate}
      />

      <RenameItemDialog
        open={renameDialog !== null}
        kind={renameDialog?.kind === 'doc' ? 'doc' : 'folder'}
        currentName={renameDialog?.name ?? ''}
        path={renameDialog?.path ?? ''}
        rewriteLeafPath
        onClose={() => setRenameDialog(null)}
        onSubmit={handleRename}
      />

      <DeleteItemDialog
        open={deleteDialog !== null}
        kind={deleteDialog?.kind === 'folder' ? 'folder' : 'doc'}
        name={deleteDialog?.name ?? ''}
        path={deleteDialog?.path ?? ''}
        descendantCount={deleteDescendantCount}
        recursive
        itemNote="and its entire revision history"
        onClose={() => setDeleteDialog(null)}
        onConfirm={handleDelete}
      />

      <MoveItemDialog
        open={moveDialog !== null}
        kind={moveDialog?.kind === 'doc' ? 'doc' : 'folder'}
        name={moveDialog?.name ?? ''}
        currentPath={moveDialog?.path ?? ''}
        folders={explorerFolders}
        dashboards={docs}
        disabledPaths={moveDisabledPaths}
        onClose={() => setMoveDialog(null)}
        onSubmit={handleMove}
      />

      <ActionErrorDialog
        open={actionError !== null}
        title={actionError?.title}
        message={actionError?.message ?? ''}
        onClose={() => setActionError(null)}
      />

      <ConflictDialog
        conflict={conflict}
        onReload={handleConflictReload}
        onDismiss={() => setConflict(null)}
      />

      <Dialog
        open={compareRevision !== null}
        onOpenChange={(next) => (!next ? setCompareRevision(null) : undefined)}
      >
        <DialogContent className="flex h-[80vh] max-w-[min(68.75rem,92vw)] flex-col gap-3 sm:max-w-[min(68.75rem,92vw)]">
          <DialogHeader>
            <DialogTitle>
              {compareRevision === null
                ? 'Compare revision'
                : `${compareRevision.author_name} · ${compareRevision.summary || 'no summary'} → current`}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-hidden rounded-md border border-line">
            {compareQuery.data === undefined ? (
              <div className="flex h-full items-center justify-center gap-2 text-sm text-ink-3">
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Loading revision…
              </div>
            ) : (
              <LazyMarkdownDiff
                original={compareQuery.data.content}
                modified={selectedDoc?.content ?? ''}
              />
            )}
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setCompareRevision(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DocsHubPage
